import type { RelayRecord, CommunityTagVote, NIP66Data, ImportSource } from '@/types/relay';

// Helper to create a deterministic uptime sparkline (seed fallback only —
// real observed uptime comes from kind:30166 history via useRelayMonitorHistory)
function makeSpark(avg: number): number[] {
  // Deterministic pseudo-random pattern seeded by the avg value itself,
  // so the same relay always shows the same stable shape instead of
  // flickering randomly on every render.
  return Array.from({ length: 14 }, (_, i) => {
    const seed = Math.sin(avg * 1000 + i * 12.9898) * 43758.5453;
    return (seed - Math.floor(seed)) < avg ? 1 : 0;
  });
}

// Helper to create community vote tags
function makeVotes(pairs: [string, number, number][]): CommunityTagVote[] {
  return pairs.map(([tag, upvotes, percent]) => ({
    tag: tag as CommunityTagVote['tag'],
    upvotes,
    percent,
  }));
}

// NIP-66 monitor pubkeys (real nostr.watch monitor pubkeys)
const MONITOR_PUBKEYS = {
  nostrWatch: 'cf45a6ba1363ad7ed213a078e710d24f2b7a9be1929acabb228084d29b3e08f8',
  // additional monitors
  altMonitor: 'a8e76c3ace7829f9ee44cf9293309e21a1824bf1e57631d00685a1ed0b0bd8a2',
};

// xport.top import source helper
function xportSource(ago: number): ImportSource {
  return { source: 'xport.top', importedAt: Date.now() - ago, fieldsUpdated: ['avgLatencyMs', 'uptimePercent30d', 'isOnline', 'countryCode'] };
}
function nip66Source(ago: number): ImportSource {
  return { source: 'nip66', importedAt: Date.now() - ago, fieldsUpdated: ['nip66', 'trustScore'] };
}

export const RELAY_SEED_DATA: RelayRecord[] = [
  {
    id: '1',
    url: 'wss://relay.damus.io',
    name: 'Damus Relay',
    description: 'The flagship relay for the Damus iOS client. High-performance, globally distributed relay with broad NIP support and excellent uptime.',
    nip11: {
      name: 'Damus Relay',
      description: 'The flagship relay for the Damus iOS client.',
      supported_nips: [1, 2, 4, 5, 9, 11, 12, 15, 16, 20, 22, 33, 40],
      software: 'nostr-rs-relay',
      version: '0.8.9',
      limitation: { max_message_length: 131072, max_subscriptions: 10, payment_required: false },
    },
    useCases: ['General', 'High Performance'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Unlimited reads', 'Public write access', 'Global CDN'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 99.7,
    uptimeSpark: makeSpark(0.997),
    avgLatencyMs: 45,
    lastChecked: Date.now() - 1000 * 60 * 5,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 365,
    featured: true,
    trustScore: 98,
    websiteUrl: 'https://damus.io',
    blossomSupported: false,
    relayToolsUrl: 'https://relay.tools/relay/wss%3A%2F%2Frelay.damus.io',
    nip66: {
      enriched: true,
      lastMonitorEvent: Date.now() - 1000 * 60 * 45,
      liveStatus: 'online',
      monitorLatencyMs: 42,
      monitorPubkey: MONITOR_PUBKEYS.nostrWatch,
      capabilities: { read: true, write: true, relay: true, blossom: false, hasNip11: true },
      conflictsWithNip11: false,
      eventsPerDay: 480000,
      connectedUsers: 12400,
    },
    importSources: [
      { source: 'nip11', importedAt: Date.now() - 1000 * 60 * 60 * 2, fieldsUpdated: ['nip11'] },
      nip66Source(1000 * 60 * 45),
      xportSource(1000 * 60 * 60 * 6),
    ],
    communityTags: makeVotes([
      ['Best for General Chat', 341, 92],
      ['High Reliability', 298, 88],
      ['Low-Latency', 210, 67],
    ]),
  },
  {
    id: '2',
    url: 'wss://relay.primal.net',
    name: 'Primal Relay',
    description: 'High-performance relay powering the Primal Nostr client. Features advanced caching and content discovery optimizations.',
    nip11: {
      name: 'Primal Relay',
      description: 'High-performance relay powering the Primal Nostr client.',
      supported_nips: [1, 2, 4, 5, 9, 11, 12, 15, 16, 20, 22, 33, 40, 45, 50],
      software: 'primal-caching-service',
      version: '1.2.0',
      limitation: { max_message_length: 262144, max_subscriptions: 20, payment_required: false },
    },
    useCases: ['General', 'High Performance'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['High performance caching', 'Content discovery', 'Read/write access'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 99.9,
    uptimeSpark: makeSpark(0.999),
    avgLatencyMs: 38,
    lastChecked: Date.now() - 1000 * 60 * 3,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 300,
    featured: true,
    trustScore: 99,
    websiteUrl: 'https://primal.net',
    blossomSupported: false,
    relayToolsUrl: 'https://relay.tools/relay/wss%3A%2F%2Frelay.primal.net',
    nip66: {
      enriched: true,
      lastMonitorEvent: Date.now() - 1000 * 60 * 30,
      liveStatus: 'online',
      monitorLatencyMs: 35,
      monitorPubkey: MONITOR_PUBKEYS.nostrWatch,
      capabilities: { read: true, write: true, relay: true, blossom: false, hasNip11: true },
      conflictsWithNip11: false,
      eventsPerDay: 920000,
      connectedUsers: 28500,
    },
    importSources: [nip66Source(1000 * 60 * 30), xportSource(1000 * 60 * 60 * 4)],
    communityTags: makeVotes([
      ['Best for General Chat', 412, 94],
      ['High Reliability', 389, 91],
      ['Low-Latency', 310, 78],
      ['Best for Developers', 189, 54],
    ]),
  },
  {
    id: '3',
    url: 'wss://nos.lol',
    name: 'nos.lol',
    description: 'Popular community relay with strong censorship resistance. No account needed, open to all.',
    nip11: {
      name: 'nos.lol',
      description: 'A Nostr relay for all.',
      supported_nips: [1, 2, 4, 5, 9, 11, 12, 15, 16, 20, 22, 33, 40],
      software: 'nostr-rs-relay',
      version: '0.8.13',
      limitation: { max_message_length: 131072, payment_required: false },
    },
    useCases: ['General', 'Censorship Resistant'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Open access', 'No account required', 'Censorship resistant'] }],
    countryCode: 'DE',
    countryName: 'Germany',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 98.8,
    uptimeSpark: makeSpark(0.988),
    avgLatencyMs: 62,
    lastChecked: Date.now() - 1000 * 60 * 7,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 400,
    featured: true,
    trustScore: 92,
    websiteUrl: 'https://nos.lol',
    blossomSupported: false,
    relayToolsUrl: 'https://relay.tools/relay/wss%3A%2F%2Fnos.lol',
    nip66: {
      enriched: true,
      lastMonitorEvent: Date.now() - 1000 * 60 * 60,
      liveStatus: 'online',
      monitorLatencyMs: 59,
      monitorPubkey: MONITOR_PUBKEYS.nostrWatch,
      capabilities: { read: true, write: true, relay: true, blossom: false, hasNip11: true },
      conflictsWithNip11: false,
      eventsPerDay: 210000,
      connectedUsers: 5800,
    },
    importSources: [nip66Source(1000 * 60 * 60), xportSource(1000 * 60 * 60 * 8)],
    communityTags: makeVotes([
      ['Censorship-Resistant', 278, 89],
      ['Best for General Chat', 241, 82],
      ['Free Tier', 310, 95],
    ]),
  },
  {
    id: '4',
    url: 'wss://relay.nostr.band',
    name: 'nostr.band Relay',
    description: 'Relay associated with nostr.band search engine. Excellent for discovery and analytics.',
    nip11: {
      name: 'nostr.band relay',
      description: 'Part of the nostr.band infrastructure.',
      supported_nips: [1, 2, 4, 9, 11, 12, 16, 20, 22, 33, 40, 45, 50],
      software: 'nostr-rs-relay',
      version: '0.9.0',
      limitation: { max_message_length: 131072, max_subscriptions: 20, payment_required: false },
    },
    useCases: ['General', 'Archive'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Full-text search support', 'Analytics integration', 'High capacity'] }],
    countryCode: 'FI',
    countryName: 'Finland',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 99.2,
    uptimeSpark: makeSpark(0.992),
    avgLatencyMs: 78,
    lastChecked: Date.now() - 1000 * 60 * 10,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 350,
    featured: false,
    trustScore: 95,
    websiteUrl: 'https://nostr.band',
  },
  {
    id: '5',
    url: 'wss://relay.nostr.bg',
    name: 'nostr.bg',
    description: 'European relay operated from Bulgaria. Good for EU users seeking low latency.',
    nip11: {
      name: 'nostr.bg',
      description: 'Nostr relay based in Bulgaria.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22],
      software: 'nostr-rs-relay',
      version: '0.8.7',
      limitation: { max_message_length: 65536, payment_required: false },
    },
    useCases: ['General'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['EU-based', 'Low latency for Europe', 'Open access'] }],
    countryCode: 'BG',
    countryName: 'Bulgaria',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 96.4,
    uptimeSpark: makeSpark(0.964),
    avgLatencyMs: 88,
    lastChecked: Date.now() - 1000 * 60 * 15,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 280,
    featured: false,
    trustScore: 85,
  },
  {
    id: '6',
    url: 'wss://nostr.wine',
    name: 'nostr.wine',
    description: 'Premium paid relay with strict quality control. Members get excellent reliability and performance. Proof-of-work required for free access.',
    nip11: {
      name: 'nostr.wine',
      description: 'A curated, members-only Nostr relay.',
      supported_nips: [1, 2, 4, 9, 11, 12, 16, 20, 22, 33, 40, 42],
      software: 'nostr-rs-relay',
      version: '0.9.1',
      limitation: { max_message_length: 131072, payment_required: true, auth_required: true },
      payments_url: 'https://nostr.wine/plans',
    },
    useCases: ['Paid Access', 'High Performance', 'Privacy'],
    priceTiers: [
      { name: 'Free (PoW)', price: 0, currency: 'USD', features: ['Read access', 'PoW required to write', 'Limited writes'] },
      { name: 'Member', price: 7, currency: 'USD', billing: 'monthly', features: ['Priority access', 'No PoW required', 'Higher limits', 'Direct support'] },
    ],
    countryCode: 'US',
    countryName: 'United States',
    isFree: false,
    isOnline: true,
    uptimePercent30d: 99.9,
    uptimeSpark: makeSpark(0.999),
    avgLatencyMs: 42,
    lastChecked: Date.now() - 1000 * 60 * 2,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 420,
    featured: true,
    trustScore: 99,
    websiteUrl: 'https://nostr.wine',
    paymentUrl: 'https://nostr.wine/plans',
    blossomSupported: false,
    relayToolsUrl: 'https://relay.tools/relay/wss%3A%2F%2Fnostr.wine',
    nip66: {
      enriched: true,
      lastMonitorEvent: Date.now() - 1000 * 60 * 20,
      liveStatus: 'online',
      monitorLatencyMs: 38,
      monitorPubkey: MONITOR_PUBKEYS.nostrWatch,
      capabilities: { read: true, write: true, relay: true, blossom: false, hasNip11: true },
      conflictsWithNip11: false,
      eventsPerDay: 95000,
      connectedUsers: 2100,
    },
    importSources: [nip66Source(1000 * 60 * 20), xportSource(1000 * 60 * 60 * 5)],
    communityTags: makeVotes([
      ['High Reliability', 189, 97],
      ['Privacy Focused', 176, 91],
      ['Best for General Chat', 142, 78],
    ]),
  },
  {
    id: '7',
    url: 'wss://purplepag.es',
    name: 'purplepag.es',
    description: 'Specialized relay optimized for storing and serving NIP-65 relay lists and user metadata. Ideal as a profile relay.',
    nip11: {
      name: 'purplepag.es',
      description: 'Relay for NIP-65 relay lists and profile data.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22],
      software: 'nostr-rs-relay',
      version: '0.8.11',
      limitation: { max_message_length: 65536, payment_required: false },
    },
    useCases: ['Inbox', 'General'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Profile relay', 'Relay list caching', 'Fast lookups'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 99.1,
    uptimeSpark: makeSpark(0.991),
    avgLatencyMs: 55,
    lastChecked: Date.now() - 1000 * 60 * 8,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 200,
    featured: false,
    trustScore: 93,
    websiteUrl: 'https://purplepag.es',
  },
  {
    id: '8',
    url: 'wss://relay.snort.social',
    name: 'Snort Relay',
    description: 'Official relay for the Snort.social web client. Optimized for general Nostr usage with strong NIP support.',
    nip11: {
      name: 'Snort Relay',
      description: 'Official relay for snort.social.',
      supported_nips: [1, 2, 4, 5, 9, 11, 12, 15, 16, 20, 22, 33, 40, 42, 45],
      software: 'nostr-rs-relay',
      version: '0.8.9',
      limitation: { max_message_length: 131072, max_subscriptions: 20, payment_required: false },
    },
    useCases: ['General'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Open read/write', 'Strong NIP support', 'Web client optimized'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 98.5,
    uptimeSpark: makeSpark(0.985),
    avgLatencyMs: 52,
    lastChecked: Date.now() - 1000 * 60 * 4,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 310,
    featured: false,
    trustScore: 91,
    websiteUrl: 'https://snort.social',
  },
  {
    id: '9',
    url: 'wss://relay.current.fyi',
    name: 'Current.fyi Relay',
    description: 'Relay for the Current Nostr client. Maintained for reliability with consistent uptime monitoring.',
    nip11: {
      name: 'Current Relay',
      description: 'Relay for the Current Nostr client.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22, 33],
      software: 'nostr-rs-relay',
      version: '0.8.8',
      limitation: { max_message_length: 131072, payment_required: false },
    },
    useCases: ['General'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Open access', 'Reliable uptime', 'Standard limits'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 97.8,
    uptimeSpark: makeSpark(0.978),
    avgLatencyMs: 60,
    lastChecked: Date.now() - 1000 * 60 * 12,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 240,
    featured: false,
    trustScore: 88,
  },
  {
    id: '10',
    url: 'wss://atlas.nostr.land',
    name: 'Atlas Relay',
    description: 'Premium paid relay network with global distribution. Subscription-based with excellent reliability and support.',
    nip11: {
      name: 'Atlas Nostr Land',
      description: 'Premium globally distributed relay network.',
      supported_nips: [1, 2, 4, 5, 9, 11, 12, 15, 16, 20, 22, 33, 40, 42],
      software: 'nostr-rs-relay',
      version: '0.9.2',
      limitation: { max_message_length: 262144, payment_required: true, auth_required: true },
      payments_url: 'https://nostr.land/plans',
    },
    useCases: ['Paid Access', 'High Performance', 'Privacy'],
    priceTiers: [
      { name: 'Personal', price: 5, currency: 'USD', billing: 'monthly', features: ['1 account', 'Global access', '5GB storage'] },
      { name: 'Pro', price: 15, currency: 'USD', billing: 'monthly', features: ['5 accounts', 'Priority routing', '50GB storage', 'API access'] },
    ],
    countryCode: 'US',
    countryName: 'United States',
    isFree: false,
    isOnline: true,
    uptimePercent30d: 99.8,
    uptimeSpark: makeSpark(0.998),
    avgLatencyMs: 33,
    lastChecked: Date.now() - 1000 * 60 * 2,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 380,
    featured: true,
    trustScore: 97,
    websiteUrl: 'https://nostr.land',
    paymentUrl: 'https://nostr.land/plans',
    blossomSupported: false,
    relayToolsUrl: 'https://relay.tools/relay/wss%3A%2F%2Fatlas.nostr.land',
    nip66: {
      enriched: true,
      lastMonitorEvent: Date.now() - 1000 * 60 * 15,
      liveStatus: 'online',
      monitorLatencyMs: 31,
      monitorPubkey: MONITOR_PUBKEYS.nostrWatch,
      capabilities: { read: true, write: true, relay: true, blossom: false, hasNip11: true },
      conflictsWithNip11: false,
      eventsPerDay: 73000,
      connectedUsers: 980,
    },
    importSources: [nip66Source(1000 * 60 * 15), xportSource(1000 * 60 * 60 * 3)],
    communityTags: makeVotes([
      ['High Reliability', 145, 95],
      ['Low-Latency', 138, 90],
      ['Best for Developers', 97, 64],
    ]),
  },
  {
    id: '11',
    url: 'wss://offchain.pub',
    name: 'offchain.pub',
    description: 'Community relay focused on free speech and censorship resistance. No restrictions on content within legal limits.',
    nip11: {
      name: 'offchain.pub',
      description: 'Free speech Nostr relay.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22],
      software: 'nostr-rs-relay',
      version: '0.8.6',
      limitation: { payment_required: false },
    },
    useCases: ['General', 'Censorship Resistant'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Free speech', 'Open access', 'No censorship'] }],
    countryCode: 'IS',
    countryName: 'Iceland',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 95.2,
    uptimeSpark: makeSpark(0.952),
    avgLatencyMs: 95,
    lastChecked: Date.now() - 1000 * 60 * 20,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 190,
    featured: false,
    trustScore: 78,
  },
  {
    id: '12',
    url: 'wss://nostr.mom',
    name: 'nostr.mom',
    description: 'Community relay with broad support and open access. Good all-around choice for general Nostr activity.',
    nip11: {
      name: 'nostr.mom',
      description: 'A community Nostr relay.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22, 33],
      software: 'nostr-rs-relay',
      version: '0.8.10',
      limitation: { max_message_length: 131072, payment_required: false },
    },
    useCases: ['General'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Open access', 'Community relay', 'General purpose'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 97.1,
    uptimeSpark: makeSpark(0.971),
    avgLatencyMs: 70,
    lastChecked: Date.now() - 1000 * 60 * 9,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 260,
    featured: false,
    trustScore: 86,
  },
  {
    id: '13',
    url: 'wss://relay.nostr.com.au',
    name: 'nostr.com.au',
    description: 'Australian relay for Nostr users in the Asia-Pacific region. Low latency for Oceania users.',
    nip11: {
      name: 'nostr.com.au',
      description: 'Australian Nostr relay.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22],
      software: 'nostr-rs-relay',
      version: '0.8.5',
      limitation: { payment_required: false },
    },
    useCases: ['General'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Australia-based', 'Low APAC latency', 'Open access'] }],
    countryCode: 'AU',
    countryName: 'Australia',
    isFree: true,
    isOnline: false,
    uptimePercent30d: 91.3,
    uptimeSpark: makeSpark(0.913),
    avgLatencyMs: 140,
    lastChecked: Date.now() - 1000 * 60 * 30,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 150,
    featured: false,
    trustScore: 72,
  },
  {
    id: '14',
    url: 'wss://relay.ditto.pub',
    name: 'Ditto Relay',
    description: 'Relay for the Ditto Nostr server platform. Supports ActivityPub bridge and advanced moderation tools.',
    nip11: {
      name: 'Ditto',
      description: 'Ditto is a Nostr server with ActivityPub bridge.',
      supported_nips: [1, 2, 4, 9, 11, 12, 16, 20, 22, 33, 40, 42, 45, 50],
      software: 'ditto',
      version: '0.5.0',
      limitation: { max_message_length: 131072, payment_required: false },
    },
    useCases: ['General', 'Communities'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['ActivityPub bridge', 'Moderation tools', 'Community features'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 98.2,
    uptimeSpark: makeSpark(0.982),
    avgLatencyMs: 48,
    lastChecked: Date.now() - 1000 * 60 * 6,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 120,
    featured: false,
    trustScore: 90,
    websiteUrl: 'https://ditto.pub',
  },
  {
    id: '15',
    url: 'wss://relay.nostr.jabber.ch',
    name: 'Jabber.ch Relay',
    description: 'Swiss relay operated by jabber.ch infrastructure. GDPR compliant, privacy focused.',
    nip11: {
      name: 'jabber.ch nostr relay',
      description: 'Swiss privacy-focused Nostr relay.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22],
      software: 'nostr-rs-relay',
      version: '0.8.9',
      limitation: { payment_required: false },
      relay_countries: ['CH'],
    },
    useCases: ['General', 'Privacy'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['GDPR compliant', 'Swiss infrastructure', 'Privacy focused'] }],
    countryCode: 'CH',
    countryName: 'Switzerland',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 97.6,
    uptimeSpark: makeSpark(0.976),
    avgLatencyMs: 75,
    lastChecked: Date.now() - 1000 * 60 * 11,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 180,
    featured: false,
    trustScore: 87,
  },
  {
    id: '16',
    url: 'wss://nostr.bitcoiner.social',
    name: 'Bitcoiner.Social',
    description: 'Community relay for Bitcoin and Nostr enthusiasts. Strong focus on the Bitcoin-adjacent Nostr community.',
    nip11: {
      name: 'bitcoiner.social',
      description: 'A Nostr relay for Bitcoin enthusiasts.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22, 33],
      software: 'nostr-rs-relay',
      version: '0.8.8',
      limitation: { payment_required: false },
    },
    useCases: ['General', 'Zaps', 'Communities'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Bitcoin community', 'Zap-friendly', 'Open access'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 96.8,
    uptimeSpark: makeSpark(0.968),
    avgLatencyMs: 65,
    lastChecked: Date.now() - 1000 * 60 * 14,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 290,
    featured: false,
    trustScore: 84,
    websiteUrl: 'https://bitcoiner.social',
  },
  {
    id: '17',
    url: 'wss://relay.nostrplebs.com',
    name: 'NostrPlebs Relay',
    description: 'Relay for the Nostr plebs community. Open access with community-oriented policies.',
    nip11: {
      name: 'NostrPlebs Relay',
      description: 'Nostr relay for the plebs.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22],
      software: 'nostr-rs-relay',
      version: '0.8.7',
      limitation: { payment_required: false },
    },
    useCases: ['General', 'Communities'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Community relay', 'Open access', 'Nostr-native'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 95.9,
    uptimeSpark: makeSpark(0.959),
    avgLatencyMs: 72,
    lastChecked: Date.now() - 1000 * 60 * 18,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 220,
    featured: false,
    trustScore: 82,
  },
  {
    id: '18',
    url: 'wss://filter.nostr.wine',
    name: 'nostr.wine Filter Relay',
    description: 'Personal inbox relay from nostr.wine. Filter and store only your events. Excellent for DM privacy.',
    nip11: {
      name: 'filter.nostr.wine',
      description: 'Personal filter relay for your events.',
      supported_nips: [1, 2, 4, 9, 11, 16, 20, 22, 42],
      software: 'nostr-rs-relay',
      version: '0.9.1',
      limitation: { payment_required: true, auth_required: true },
      payments_url: 'https://nostr.wine/plans',
    },
    useCases: ['Inbox', 'DMs', 'Privacy', 'Paid Access'],
    priceTiers: [
      { name: 'Included with Wine', price: 7, currency: 'USD', billing: 'monthly', features: ['Personal inbox', 'DM filtering', 'Event storage', 'Auth required'] },
    ],
    countryCode: 'US',
    countryName: 'United States',
    isFree: false,
    isOnline: true,
    uptimePercent30d: 99.8,
    uptimeSpark: makeSpark(0.998),
    avgLatencyMs: 40,
    lastChecked: Date.now() - 1000 * 60 * 3,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 160,
    featured: false,
    trustScore: 96,
    websiteUrl: 'https://nostr.wine',
    paymentUrl: 'https://nostr.wine/plans',
  },
  {
    id: '19',
    url: 'wss://relay.nostrcheck.me',
    name: 'NostrCheck.me Relay',
    description: 'Relay for the NostrCheck.me verification service. Optimized for profile and metadata events.',
    nip11: {
      name: 'nostrcheck.me relay',
      description: 'Relay supporting the NostrCheck.me ecosystem.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22, 33, 40],
      software: 'nostrcheck-server',
      version: '2.0',
      limitation: { payment_required: false },
    },
    useCases: ['General'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Open access', 'Verification-ready', 'Standard NIP support'] }],
    countryCode: 'ES',
    countryName: 'Spain',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 97.4,
    uptimeSpark: makeSpark(0.974),
    avgLatencyMs: 82,
    lastChecked: Date.now() - 1000 * 60 * 16,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 140,
    featured: false,
    trustScore: 85,
    websiteUrl: 'https://nostrcheck.me',
  },
  {
    id: '20',
    url: 'wss://relay.wellorder.net',
    name: 'WellOrder Relay',
    description: 'Long-running community relay focused on well-ordered events and reliable data delivery.',
    nip11: {
      name: 'wellorder relay',
      description: 'A reliable community Nostr relay.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22],
      software: 'nostr-rs-relay',
      version: '0.8.6',
      limitation: { payment_required: false },
    },
    useCases: ['General', 'Archive'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Long-term storage', 'Reliable delivery', 'Open access'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 96.1,
    uptimeSpark: makeSpark(0.961),
    avgLatencyMs: 80,
    lastChecked: Date.now() - 1000 * 60 * 22,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 450,
    featured: false,
    trustScore: 83,
  },
  {
    id: '21',
    url: 'wss://relay.nostr.directory',
    name: 'Nostr Directory Relay',
    description: 'Directory-focused relay that stores profiles and contact lists. Good for NIP-05 lookups.',
    nip11: {
      name: 'Nostr Directory',
      description: 'Relay for Nostr directory services.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22],
      software: 'nostr-rs-relay',
      version: '0.8.9',
      limitation: { payment_required: false },
    },
    useCases: ['General'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Profile storage', 'NIP-05 support', 'Open access'] }],
    countryCode: 'GB',
    countryName: 'United Kingdom',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 98.1,
    uptimeSpark: makeSpark(0.981),
    avgLatencyMs: 67,
    lastChecked: Date.now() - 1000 * 60 * 8,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 200,
    featured: false,
    trustScore: 89,
  },
  {
    id: '22',
    url: 'wss://relay.nostr.net',
    name: 'nostr.net',
    description: 'One of the oldest running Nostr relays. Archival relay with extensive event history.',
    nip11: {
      name: 'nostr.net relay',
      description: 'Original Nostr relay infrastructure.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20],
      software: 'nostr-rs-relay',
      version: '0.7.0',
      limitation: { payment_required: false },
    },
    useCases: ['General', 'Archive'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Archival relay', 'Long event history', 'Open access'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 94.8,
    uptimeSpark: makeSpark(0.948),
    avgLatencyMs: 90,
    lastChecked: Date.now() - 1000 * 60 * 25,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 600,
    featured: false,
    trustScore: 79,
  },
  {
    id: '23',
    url: 'wss://relay.nostr.hu',
    name: 'nostr.hu',
    description: 'Hungarian Nostr relay with strong community focus in Central Europe.',
    nip11: {
      name: 'nostr.hu',
      description: 'Hungarian Nostr relay.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22],
      software: 'nostr-rs-relay',
      version: '0.8.7',
      limitation: { payment_required: false },
    },
    useCases: ['General'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Central European location', 'Community relay', 'Open access'] }],
    countryCode: 'HU',
    countryName: 'Hungary',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 96.5,
    uptimeSpark: makeSpark(0.965),
    avgLatencyMs: 85,
    lastChecked: Date.now() - 1000 * 60 * 13,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 170,
    featured: false,
    trustScore: 82,
  },
  {
    id: '24',
    url: 'wss://wot.nostr.sats4.life',
    name: 'sats4.life WoT Relay',
    description: 'Web-of-trust filtered relay. Only accepts events from users within the trusted network. Spam-free environment.',
    nip11: {
      name: 'sats4.life WoT Relay',
      description: 'Web-of-trust filtered Nostr relay.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22, 42],
      software: 'strfry',
      version: '1.0',
      limitation: { payment_required: false, restricted_writes: true },
    },
    useCases: ['General', 'Privacy', 'High Performance'],
    priceTiers: [{ name: 'Free (WoT)', price: 0, currency: 'USD', features: ['Spam-free', 'WoT filtered', 'High signal-to-noise'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 98.7,
    uptimeSpark: makeSpark(0.987),
    avgLatencyMs: 55,
    lastChecked: Date.now() - 1000 * 60 * 7,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 90,
    featured: false,
    trustScore: 91,
  },
  {
    id: '25',
    url: 'wss://relay.nostr.jabber.ch',
    name: 'Jabber CH Relay (2)',
    description: 'European privacy relay.',
    nip11: {
      name: 'jabber.ch',
      description: 'European privacy relay.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20],
      limitation: { payment_required: false },
    },
    useCases: ['General', 'Privacy'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['EU privacy', 'Open access'] }],
    countryCode: 'CH',
    countryName: 'Switzerland',
    isFree: true,
    isOnline: false,
    uptimePercent30d: 89.2,
    uptimeSpark: makeSpark(0.892),
    avgLatencyMs: 100,
    lastChecked: Date.now() - 1000 * 60 * 40,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 130,
    featured: false,
    trustScore: 70,
  },
  // ─── Blossom / Media-focused relay ─────────────────────────────────────────
  {
    id: '26',
    url: 'wss://blossom.band',
    name: 'Blossom Band',
    description: 'Media-optimized relay with native Blossom server support for image and video uploads. First-class NIP-94 and NIP-96 implementation.',
    nip11: {
      name: 'Blossom Band',
      description: 'Blossom media relay with NIP-94/96 support.',
      supported_nips: [1, 2, 9, 11, 16, 20, 94, 96],
      software: 'blossom-server',
      version: '1.0.0',
      limitation: { max_message_length: 524288, payment_required: false },
    },
    useCases: ['Blossom', 'Images', 'Video'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Blossom uploads', 'NIP-94 events', 'NIP-96 compatible', 'Media CDN'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 97.5,
    uptimeSpark: makeSpark(0.975),
    avgLatencyMs: 58,
    lastChecked: Date.now() - 1000 * 60 * 5,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 60,
    featured: true,
    trustScore: 88,
    blossomSupported: true,
    relayToolsUrl: 'https://relay.tools/relay/wss%3A%2F%2Fblossom.band',
    nip66: {
      enriched: true,
      lastMonitorEvent: Date.now() - 1000 * 60 * 25,
      liveStatus: 'online',
      monitorLatencyMs: 55,
      monitorPubkey: MONITOR_PUBKEYS.nostrWatch,
      capabilities: { read: true, write: true, relay: true, blossom: true, hasNip11: true },
      conflictsWithNip11: false,
      eventsPerDay: 34000,
      connectedUsers: 820,
    },
    importSources: [nip66Source(1000 * 60 * 25), xportSource(1000 * 60 * 60 * 10)],
    communityTags: makeVotes([
      ['Best for Images', 312, 96],
      ['Best for Video/Blossom', 298, 94],
      ['Free Tier', 210, 89],
    ]),
  },
  // ─── strfry-based high-volume relay ────────────────────────────────────────
  {
    id: '27',
    url: 'wss://relay.noswhere.com',
    name: 'Noswhere',
    description: 'High-volume relay running strfry for maximum throughput. Excellent for high-traffic applications and developers.',
    nip11: {
      name: 'Noswhere',
      description: 'High-volume strfry relay.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22, 29, 33, 40, 42, 45, 50],
      software: 'strfry',
      version: '1.0.2',
      limitation: { max_message_length: 131072, max_subscriptions: 50, payment_required: false },
    },
    useCases: ['General', 'High Performance', 'Communities'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['strfry engine', 'High throughput', 'NIP-29 groups', 'Open access'] }],
    countryCode: 'NL',
    countryName: 'Netherlands',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 98.9,
    uptimeSpark: makeSpark(0.989),
    avgLatencyMs: 49,
    lastChecked: Date.now() - 1000 * 60 * 4,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 110,
    featured: false,
    trustScore: 90,
    blossomSupported: false,
    nip66: {
      enriched: true,
      lastMonitorEvent: Date.now() - 1000 * 60 * 40,
      liveStatus: 'online',
      monitorLatencyMs: 47,
      monitorPubkey: MONITOR_PUBKEYS.altMonitor,
      capabilities: { read: true, write: true, relay: true, blossom: false, hasNip11: true },
      conflictsWithNip11: false,
      eventsPerDay: 550000,
      connectedUsers: 9200,
    },
    importSources: [nip66Source(1000 * 60 * 40), xportSource(1000 * 60 * 60 * 6)],
    communityTags: makeVotes([
      ['Best for High-Volume', 198, 91],
      ['Best for Developers', 176, 84],
      ['Low-Latency', 162, 79],
    ]),
  },
  // ─── Long-form / blogging relay ────────────────────────────────────────────
  {
    id: '28',
    url: 'wss://relay.satellite.earth',
    name: 'Satellite.Earth Relay',
    description: 'Relay powering the satellite.earth long-form publishing platform. Optimized for NIP-23 articles, NIP-23 edits, and long-form Nostr content.',
    nip11: {
      name: 'Satellite Earth',
      description: 'Long-form content relay for satellite.earth.',
      supported_nips: [1, 2, 9, 11, 16, 20, 22, 23, 33],
      software: 'nostr-rs-relay',
      version: '0.8.11',
      limitation: { max_message_length: 524288, payment_required: false },
    },
    useCases: ['Long Form', 'General'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Long-form focus', 'Article storage', 'Editing support', 'Open access'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 97.2,
    uptimeSpark: makeSpark(0.972),
    avgLatencyMs: 64,
    lastChecked: Date.now() - 1000 * 60 * 11,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 75,
    featured: false,
    trustScore: 86,
    blossomSupported: false,
    nip66: {
      enriched: false,
    },
    importSources: [{ source: 'nip11', importedAt: Date.now() - 1000 * 60 * 60, fieldsUpdated: ['nip11'] }, xportSource(1000 * 60 * 60 * 12)],
    communityTags: makeVotes([
      ['Best for Long-Form', 142, 93],
      ['Best for Developers', 89, 66],
    ]),
  },
  // ─── NIP-66 conflict example (for moderation demo) ─────────────────────────
  {
    id: '29',
    url: 'wss://relay.lexingtonbitcoin.org',
    name: 'Lexington Bitcoin Relay',
    description: 'Community relay operated by the Lexington Bitcoin meetup group. NIP-66 data shows slight uptime discrepancy under review.',
    nip11: {
      name: 'Lexington Bitcoin',
      description: 'Community relay for Bitcoiners.',
      supported_nips: [1, 2, 4, 9, 11, 15, 16, 20, 22, 57],
      software: 'nostr-rs-relay',
      version: '0.8.8',
      limitation: { payment_required: false },
    },
    useCases: ['General', 'Zaps'],
    priceTiers: [{ name: 'Free', price: 0, currency: 'USD', features: ['Bitcoin community', 'Zap support', 'Open access'] }],
    countryCode: 'US',
    countryName: 'United States',
    isFree: true,
    isOnline: true,
    uptimePercent30d: 94.1,
    uptimeSpark: makeSpark(0.941),
    avgLatencyMs: 77,
    lastChecked: Date.now() - 1000 * 60 * 22,
    addedAt: Date.now() - 1000 * 60 * 60 * 24 * 95,
    featured: false,
    trustScore: 76,
    blossomSupported: false,
    nip66: {
      enriched: true,
      lastMonitorEvent: Date.now() - 1000 * 60 * 90,
      liveStatus: 'online',
      monitorLatencyMs: 78,
      monitorPubkey: MONITOR_PUBKEYS.altMonitor,
      // NIP-11 says 94.1% uptime; NIP-66 monitor claims 97.3% — flagged
      conflictsWithNip11: true,
      conflictDetail: 'NIP-66 monitor reports 97.3% uptime vs 94.1% from our own checks. Under review.',
      capabilities: { read: true, write: true, relay: true, blossom: false, hasNip11: true },
      eventsPerDay: 12000,
      connectedUsers: 310,
    },
    importSources: [nip66Source(1000 * 60 * 90)],
    communityTags: makeVotes([
      ['Best for Zaps', 67, 78],
    ]),
  },
];

export const COUNTRIES = Array.from(
  new Set(RELAY_SEED_DATA.filter((r) => r.countryCode).map((r) => ({ code: r.countryCode!, name: r.countryName || r.countryCode! })))
).filter((v, i, a) => a.findIndex((t) => t.code === v.code) === i);

export const USE_CASE_OPTIONS: string[] = [
  'General', 'Images', 'Video', 'Blossom', 'Paid Access', 'High Performance',
  'Censorship Resistant', 'Communities', 'Long Form', 'Marketplace', 'Gaming',
  'Privacy', 'Archive', 'Inbox', 'DMs', 'Zaps',
];

export const STATS = {
  total: RELAY_SEED_DATA.length,
  online: RELAY_SEED_DATA.filter((r) => r.isOnline).length,
  free: RELAY_SEED_DATA.filter((r) => r.isFree).length,
  paid: RELAY_SEED_DATA.filter((r) => !r.isFree).length,
  nip66Enriched: RELAY_SEED_DATA.filter((r) => r.nip66?.enriched).length,
  blossomEnabled: RELAY_SEED_DATA.filter((r) => r.blossomSupported).length,
};
